import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Response } from '@ui/src';
import {
  AMLBotAssets,
  CASPAlias,
  PSPDetails,
  PSPValidator,
  RateSource,
} from '../../types';
import { pspDetailsApi } from '../services/details';
import { applicationShemesApi } from '../services/application-shemes';

export interface PSPDetailsState {
  validators?: PSPValidator;
  pspDetails?: PSPDetails[];
  rateSources?: RateSource[];
  isDirty?: boolean;
  caspAliases?: CASPAlias[];
  amlBotAssets?: AMLBotAssets;
}

const initialState: PSPDetailsState = {};

export const pspDetailsSlice = createSlice({
  name: 'pspDetails',
  initialState,
  reducers: {
    setAdministationRateSources: (
      state,
      action: PayloadAction<RateSource[]>,
    ) => {
      state.rateSources = action.payload;
    },
    setAdministationPspDetails: (
      state,
      action: PayloadAction<PSPDetails[]>,
    ) => {
      state.pspDetails = action.payload;
    },
    setIsDirty: (state, action: PayloadAction<boolean>) => {
      state.isDirty = action.payload;
    },
    setCaspAliases: (state, action: PayloadAction<CASPAlias[]>) => {
      state.caspAliases = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      pspDetailsApi.endpoints.getPSPDetails.matchFulfilled,
      (state, action: PayloadAction<Response<PSPDetails>>) => {
        state.pspDetails = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      pspDetailsApi.endpoints.getCASPAliases.matchFulfilled,
      (state, action: PayloadAction<Response<CASPAlias>>) => {
        state.caspAliases = action.payload.data.edges?.map((item) => item.node);
      },
    );
    builder.addMatcher(
      pspDetailsApi.endpoints.getPSPDetails.matchFulfilled,
      (state, action: PayloadAction<Response<PSPDetails>>) => {
        state.pspDetails = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      pspDetailsApi.endpoints.getRateSources.matchFulfilled,
      (state, action: PayloadAction<Response<RateSource>>) => {
        state.rateSources = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      applicationShemesApi.endpoints.editApplicationSchemeSettings
        .matchFulfilled,
      (state, action: PayloadAction<{ data: PSPDetails }>) => {
        state.pspDetails = state.pspDetails?.map((item) => {
          let result = { ...item };

          if (result.id === action.payload.data.id) {
            result = action.payload.data;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      pspDetailsApi.endpoints.editBeneficiarySettings.matchFulfilled,
      (state, action: PayloadAction<{ data: PSPDetails }>) => {
        state.pspDetails = state.pspDetails?.map((item) => {
          let result = { ...item };

          if (result.id === action.payload.data.id) {
            result = action.payload.data;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      pspDetailsApi.endpoints.getAMLBotAssets.matchFulfilled,
      (
        state,
        action: PayloadAction<{ data: { modesAssets: AMLBotAssets } }>,
      ) => {
        state.amlBotAssets = action.payload.data.modesAssets;
      },
    );
    builder.addMatcher(
      pspDetailsApi.endpoints.deleteCASPAlias.matchFulfilled,
      (state, action: PayloadAction<{ data: CASPAlias }>) => {
        state.caspAliases = state.caspAliases?.filter(
          (item) => item.id !== action.payload.data.id,
        );
      },
    );
    builder.addMatcher(
      pspDetailsApi.endpoints.editBeneficiarySettings.matchFulfilled,
      (state, action: PayloadAction<{ data: PSPDetails }>) => {
        state.pspDetails = state.pspDetails?.map((item) => {
          let result = { ...item };

          if (result.id === action.payload.data.id) {
            result = action.payload.data;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      pspDetailsApi.endpoints.editPSPDetails.matchFulfilled,
      (state, action: PayloadAction<{ data: PSPDetails }>) => {
        state.pspDetails = state.pspDetails?.map((item) => {
          let result = { ...item };

          if (result.id === action.payload.data.id) {
            result = action.payload.data;
          }

          return result;
        });
      },
    );
  },
});

export const {
  setAdministationRateSources,
  setAdministationPspDetails,
  setIsDirty,
  setCaspAliases,
} = pspDetailsSlice.actions;

export default pspDetailsSlice.reducer;
